/**
 * Screen 1: Leads Inbox — /leads
 *
 * UX principles (TZ 9.4):
 * - Separate from pipeline/kanban (TZ 9.4 #4, 9.5)
 * - "Взял/Отказ" only on new_lead stage (TZ 2.4, 5.6)
 * - Urgency color signals (TZ 9.4 #8)
 * - Badge "повторный" for is_repeat
 * - ≤5 fields per row
 *
 * claimed_by semantics (TZ 2.4, 3.7):
 * - claimed_by only prevents double-call on first contact
 * - Does NOT restrict any other actions after claim
 * - Only reset by "Отказ" (release)
 */

import { useState } from 'react'
import { Link } from '@tanstack/react-router'
import { UserCheck, UserX, Clock, AlertCircle, Inbox } from 'lucide-react'
import { useLeadsList, useClaimLead, useReleaseLead } from '@/hooks/useLeads'
import { useAuth } from '@/hooks/useAuth'
import { useActiveClient } from '@/hooks/useClients'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { formatRelativeTime, getLeadUrgencyClass } from '@/lib/utils'
import { leadsApi } from '@/api/client'
import type { LeadListItem } from '@/types'

type FilterMode = 'all' | 'unclaimed'

// ---- Manual lead form ----

function ManualLeadForm({
  onClose,
  tenantId,
}: {
  onClose: () => void
  tenantId: string | null
}) {
  const [name, setName] = useState('')
  const [phone, setPhone] = useState('')
  const [niche, setNiche] = useState('')
  const [submitting, setSubmitting] = useState(false)
  const [error, setError] = useState<string | null>(null)

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault()
    // Бэк допускает display_name ИЛИ phone — требуем хотя бы одно.
    if (!name.trim() && !phone.trim()) { setError('Укажите имя или телефон'); return }
    setSubmitting(true)
    setError(null)
    try {
      await leadsApi.createManual({
        display_name: name.trim() || undefined,
        phone: phone.trim() || undefined,
        business_niche: niche.trim() || undefined,
        source: 'manual',
        // Активный клиент уходит явным полем tenant_id (не строкой в note).
        ...(tenantId ? { tenant_id: tenantId } : {}),
      })
      onClose()
    } catch {
      setError('Ошибка при создании лида')
    } finally {
      setSubmitting(false)
    }
  }

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40">
      <Card className="w-full max-w-md mx-4">
        <CardHeader className="pb-3">
          <CardTitle className="text-base">Добавить лид вручную</CardTitle>
          {tenantId && (
            <p className="text-xs text-muted-foreground">Клиент: {tenantId}</p>
          )}
        </CardHeader>
        <CardContent>
          <form onSubmit={handleSubmit} className="space-y-3">
            <p className="text-xs text-muted-foreground">Укажите имя или телефон (хотя бы одно).</p>
            <div>
              <label className="text-sm font-medium block mb-1">Имя / название</label>
              <input
                type="text"
                value={name}
                onChange={(e) => setName(e.target.value)}
                placeholder="Иван Иванов"
                className="w-full rounded-md border px-3 py-2 text-sm outline-none focus:ring-2 focus:ring-ring"
                autoFocus
              />
            </div>
            <div>
              <label className="text-sm font-medium block mb-1">Телефон</label>
              <input
                type="tel"
                value={phone}
                onChange={(e) => setPhone(e.target.value)}
                placeholder="+7 900 000 0000"
                className="w-full rounded-md border px-3 py-2 text-sm outline-none focus:ring-2 focus:ring-ring"
              />
            </div>
            <div>
              <label className="text-sm font-medium block mb-1">Ниша (опционально)</label>
              <input
                type="text"
                value={niche}
                onChange={(e) => setNiche(e.target.value)}
                placeholder="Стоматология, стройка..."
                className="w-full rounded-md border px-3 py-2 text-sm outline-none focus:ring-2 focus:ring-ring"
              />
            </div>
            {error && <p className="text-sm text-destructive">{error}</p>}
            <div className="flex gap-2 justify-end pt-2">
              <Button type="button" variant="outline" size="sm" onClick={onClose}>Отмена</Button>
              <Button type="submit" size="sm" disabled={submitting}>
                {submitting ? 'Добавляем...' : 'Добавить'}
              </Button>
            </div>
          </form>
        </CardContent>
      </Card>
    </div>
  )
}

const SOURCE_LABELS: Record<string, string> = {
  telegram: 'TG',
  max: 'MAX',
  web_form: 'Форма',
  avito: 'Авито',
  manual: 'Вручную',
}

// Urgency color classes
const urgencyRowClass: Record<string, string> = {
  'urgency-red': 'border-l-4 border-red-400',
  'urgency-yellow': 'border-l-4 border-amber-400',
}

function LeadRow({
  lead,
  currentUserId,
  onClaim,
  onRelease,
  isClaiming,
  isReleasing,
}: {
  lead: LeadListItem
  currentUserId: string
  onClaim: (dealId: string) => void
  onRelease: (dealId: string) => void
  isClaiming: boolean
  isReleasing: boolean
}) {
  const urgency = getLeadUrgencyClass(lead.created_at, lead.claimed_by)
  const isClaimedByMe = lead.claimed_by === currentUserId

  return (
    <div
      className={`flex items-center gap-4 rounded-lg border bg-card p-4 hover:bg-accent/30 transition-colors ${urgency ? urgencyRowClass[urgency] ?? '' : ''}`}
    >
      {/* Urgency icon */}
      <div className="shrink-0 w-6">
        {urgency === 'urgency-red' && <AlertCircle className="h-5 w-5 text-red-500" />}
        {urgency === 'urgency-yellow' && <Clock className="h-5 w-5 text-amber-500" />}
      </div>

      {/* Main info: display_name + niche + first_message (≤5 fields) */}
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-2 flex-wrap">
          <Link
            to="/contact/$id"
            params={{ id: lead.deal_id }}
            className="font-medium text-sm hover:underline truncate"
          >
            {lead.display_name}
          </Link>
          {lead.is_repeat && <Badge variant="repeat">повторный</Badge>}
          <Badge variant="outline" className="text-xs">
            {SOURCE_LABELS[lead.source_channel] ?? lead.source_channel}
          </Badge>
          {lead.contact_handle && (
            <span className="text-xs text-muted-foreground">{lead.contact_handle}</span>
          )}
        </div>
        {lead.business_niche && (
          <p className="text-xs text-muted-foreground mt-0.5">{lead.business_niche}</p>
        )}
        {lead.first_message && (
          <p className="text-xs text-muted-foreground mt-1 line-clamp-1 italic">
            «{lead.first_message}»
          </p>
        )}
      </div>

      {/* Time */}
      <div className="text-xs text-muted-foreground shrink-0 hidden sm:block">
        {formatRelativeTime(lead.created_at)}
      </div>

      {/* Claim status + action buttons */}
      <div className="flex items-center gap-2 shrink-0">
        {lead.claimed_by ? (
          <>
            <span className="text-xs text-muted-foreground">
              взял <strong>{lead.claimed_by_name ?? 'кто-то'}</strong>
            </span>
            {isClaimedByMe && (
              <Button
                variant="release"
                size="sm"
                onClick={() => onRelease(lead.deal_id)}
                disabled={isReleasing}
              >
                <UserX className="h-4 w-4" />
                Отказ
              </Button>
            )}
          </>
        ) : (
          <Button
            variant="claim"
            size="sm"
            onClick={() => onClaim(lead.deal_id)}
            disabled={isClaiming}
          >
            <UserCheck className="h-4 w-4" />
            Взял
          </Button>
        )}

        <Link
          to="/contact/$id"
          params={{ id: lead.deal_id }}
          className="text-xs text-primary hover:underline hidden md:inline"
        >
          Открыть
        </Link>
      </div>
    </div>
  )
}

export function LeadsPage() {
  const [filter, setFilter] = useState<FilterMode>('all')
  const [showManual, setShowManual] = useState(false)
  const [toast, setToast] = useState<{ message: string; type: 'error' | 'success' } | null>(null)

  const { user } = useAuth()
  const { activeClientId } = useActiveClient()
  const currentUserId = user?.id ?? ''

  const { data: leads = [], isLoading, error } = useLeadsList(filter)
  const claimMutation = useClaimLead()
  const releaseMutation = useReleaseLead()

  function showToast(message: string, type: 'error' | 'success' = 'success') {
    setToast({ message, type })
    setTimeout(() => setToast(null), 4000)
  }

  function handleClaim(dealId: string) {
    claimMutation.mutate(dealId, {
      onSuccess: (res) => {
        // ClaimResponse.ok=false при гонке — показываем, кто уже взял
        if (res.ok) showToast('Лид взят в работу', 'success')
        else showToast(res.message ?? 'Лид уже взят другим оператором', 'error')
      },
      onError: () => showToast('Ошибка при взятии лида', 'error'),
    })
  }

  function handleRelease(dealId: string) {
    releaseMutation.mutate(dealId, {
      onSuccess: (res) => {
        if (res.ok) showToast('Лид отпущен', 'success')
        else showToast(res.message ?? 'Не удалось отпустить лид', 'error')
      },
      onError: () => showToast('Ошибка при отпуске лида', 'error'),
    })
  }

  if (isLoading) return <div className="p-4 text-muted-foreground">Загрузка лидов...</div>
  if (error) return <div className="p-4 text-destructive">Ошибка загрузки лидов</div>

  const unclaimedCount = leads.filter((l) => !l.claimed_by).length

  return (
    <div className="space-y-4">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-xl font-semibold">Лиды</h1>
          <p className="text-sm text-muted-foreground">
            {unclaimedCount > 0
              ? `${unclaimedCount} ждут взятия`
              : 'Нет необработанных лидов'}
          </p>
        </div>
        <Button variant="default" size="sm" onClick={() => setShowManual(true)}>+ Лид</Button>
      </div>

      {/* Filter tabs */}
      <div className="flex gap-2 border-b pb-2">
        {(['all', 'unclaimed'] as FilterMode[]).map((f) => (
          <button
            key={f}
            onClick={() => setFilter(f)}
            className={`text-sm px-3 py-1 rounded-md transition-colors ${filter === f ? 'bg-accent text-accent-foreground font-medium' : 'text-muted-foreground hover:bg-accent/50'}`}
          >
            {f === 'all' ? 'Все' : 'Ждут взятия'}
            {f === 'unclaimed' && unclaimedCount > 0 && (
              <span className="ml-1.5 inline-flex h-4 w-4 items-center justify-center rounded-full bg-red-500 text-white text-[10px]">
                {unclaimedCount}
              </span>
            )}
          </button>
        ))}
      </div>

      {/* Leads list */}
      {leads.length === 0 ? (
        <div className="flex flex-col items-center justify-center rounded-lg border border-dashed py-16 text-center">
          <Inbox className="h-10 w-10 text-muted-foreground mb-3" />
          <p className="text-sm text-muted-foreground">Лидов нет</p>
          <Button variant="default" size="sm" className="mt-4" onClick={() => setShowManual(true)}>
            + Добавить лид вручную
          </Button>
        </div>
      ) : (
        <div className="space-y-2">
          {leads.map((lead) => (
            <LeadRow
              key={lead.deal_id}
              lead={lead}
              currentUserId={currentUserId}
              onClaim={handleClaim}
              onRelease={handleRelease}
              isClaiming={claimMutation.isPending}
              isReleasing={releaseMutation.isPending}
            />
          ))}
        </div>
      )}

      {showManual && (
        <ManualLeadForm
          onClose={() => setShowManual(false)}
          tenantId={activeClientId}
        />
      )}

      {toast && (
        <div
          className={`fixed bottom-6 right-6 z-50 flex items-center gap-3 rounded-lg border px-4 py-3 shadow-lg ${
            toast.type === 'error' ? 'border-red-200 bg-red-50' : 'border-green-200 bg-green-50'
          }`}
        >
          <p className={`text-sm font-medium ${toast.type === 'error' ? 'text-red-700' : 'text-green-700'}`}>
            {toast.message}
          </p>
          <button
            onClick={() => setToast(null)}
            className={`ml-2 text-xs ${toast.type === 'error' ? 'text-red-500 hover:text-red-700' : 'text-green-500 hover:text-green-700'}`}
          >
            ✕
          </button>
        </div>
      )}
    </div>
  )
}
